import { platformApprovedActionProviderDescriptorV2Schema, platformApprovedActionProviderRequestV2Schema, platformApprovedActionProviderResultV2Schema, platformApprovedActionProviderSlot, platformReceiptBoundActionProviderHostCapability, } from '@crest/platform-contracts/hosts';
import { z } from 'zod';
import { insuranceClaimsIdSchema } from './insurance-claims-claim.contract.js';
export const insuranceClaimsAssistantActionProviderId = 'insurance-claims.assistant-actions';
export const insuranceClaimsAssistantActionProviderCapability = 'insurance-claims.assistant-actions.v1';
export const insuranceClaimsAssistantActionSlot = platformApprovedActionProviderSlot;
const optionalClearableText = (maximum) => z.string().trim().min(1).max(maximum).nullable().optional();
/**
 * Claim-owned merge fields carried by an approved legacy Project update.
 * `undefined` preserves the current immutable claim version's value; `null`
 * explicitly clears it. Peril type and water-loss facts are deliberately
 * absent: they are never inferred from Project type or the Project water-job
 * designation.
 */
const insuranceClaimsAssistantClaimFieldsShape = {
    carrierDisplayName: optionalClearableText(240),
    claimNumber: optionalClearableText(120),
    policyNumber: optionalClearableText(120),
    adjusterDisplayName: optionalClearableText(240),
    adjusterPhone: optionalClearableText(80),
    adjusterEmail: z.string().trim().email().max(320).nullable().optional(),
    lossDate: z.string().date().nullable().optional(),
};
export const insuranceClaimsAssistantClaimFieldKeys = Object.keys(insuranceClaimsAssistantClaimFieldsShape);
/**
 * Creates a minimal open claim for a Project that has none, optionally seeded
 * with approved claim-owned facts. The action target is the Project.
 */
export const insuranceClaimsAssistantClaimCreateActionId = 'insurance-claims.claim-create.v1';
export const insuranceClaimsAssistantClaimCreateInputSchema = z.strictObject({
    claimId: insuranceClaimsIdSchema,
    ...insuranceClaimsAssistantClaimFieldsShape,
});
/**
 * sha256 of the canonical JSON encoding of the create input schema's
 * JSON-Schema artifact; recomputed by the contract test.
 */
export const insuranceClaimsAssistantClaimCreateInputSchemaHash = '1f4c46ebf81f04bca33b5dd6032e0ef9ffeed8e7c76a166a5b883631f763c020';
/**
 * Merges approved claim-owned facts into an existing open claim as a new
 * immutable claim version. The action target is the claim.
 */
export const insuranceClaimsAssistantClaimReviseActionId = 'insurance-claims.claim-revise.v1';
export const insuranceClaimsAssistantClaimReviseInputSchema = z.strictObject({
    projectId: insuranceClaimsIdSchema,
    expectedRevision: z.number().int().positive(),
    ...insuranceClaimsAssistantClaimFieldsShape,
}).refine((value) => insuranceClaimsAssistantClaimFieldKeys.some((field) => value[field] !== undefined), 'At least one claim field must be changed.');
export const insuranceClaimsAssistantClaimReviseInputSchemaHash = '8e25a751d6120b289f7ee19627c50afb105930573729b1df3a8c760cbbf1d617';
const insuranceClaimsAssistantOwnerSafetyBaseShape = {
    targetClaimId: insuranceClaimsIdSchema,
    originalExecutionId: z.string().uuid(),
    // A successful archive/revise increments one PostgreSQL int4 revision.
    expectedRevision: z.number().int().positive().max(2_147_483_646),
    reason: z.string().trim().min(10).max(500),
};
/**
 * Owner-specific compensation for one exact Assistant-created claim. Claims
 * proves the immutable create origin and archives the still-current claim;
 * callers cannot cancel, delete, or select an arbitrary claim mutation.
 */
export const insuranceClaimsAssistantArchiveCreatedClaimActionId = 'insurance-claims.archive-assistant-created-claim.v1';
export const insuranceClaimsAssistantArchiveCreatedClaimInputSchema = z.strictObject({
    ...insuranceClaimsAssistantOwnerSafetyBaseShape,
    compensationSummary: z.string().trim().min(1).max(500),
});
export const insuranceClaimsAssistantArchiveCreatedClaimInputSchemaHash = '95b18bbf8e1eada1ce5db166199b879d0d86950f3a24eea7cea97b6f68ecf2f4';
/**
 * Owner-specific supersession for one exact Assistant claim revision. Claims
 * derives the prior content from its immutable version evidence and appends a
 * new version; no caller-supplied inverse claim payload is accepted.
 */
export const insuranceClaimsAssistantRestoreClaimRevisionActionId = 'insurance-claims.supersede-assistant-claim-revision.v1';
export const insuranceClaimsAssistantRestoreClaimRevisionInputSchema = z.strictObject({
    ...insuranceClaimsAssistantOwnerSafetyBaseShape,
    restorationSummary: z.string().trim().min(1).max(500),
});
export const insuranceClaimsAssistantRestoreClaimRevisionInputSchemaHash = 'b20ad3fd1bfd36a15639e56bf0a9beb18c6f79275a4a75b370bf807013bbbb4c';
export const insuranceClaimsAssistantActionProviderDescriptor = platformApprovedActionProviderDescriptorV2Schema.parse({
    kind: 'descriptor',
    contractVersion: platformReceiptBoundActionProviderHostCapability,
    providerId: insuranceClaimsAssistantActionProviderId,
    providerCapability: insuranceClaimsAssistantActionProviderCapability,
    displayName: 'Insurance Claims approved actions',
    actions: [{
            actionId: insuranceClaimsAssistantClaimCreateActionId,
            displayName: 'Create insurance claim',
            targetResourceType: 'projects.project',
            inputSchemaHash: insuranceClaimsAssistantClaimCreateInputSchemaHash,
            requiredPermission: 'insurance-claims.claims.manage',
            // The owner-specific action proves the exact create execution and
            // archives its still-current claim while retaining immutable history.
            assistantPolicy: {
                policyContractVersion: 'platform.assistant-action-policy.v1',
                confirmation: 'explicit',
                effectClass: 'compensating-write',
                externalEffectClass: 'none',
                reversal: {
                    kind: 'compensating-action',
                    registeredReversalActionId: insuranceClaimsAssistantArchiveCreatedClaimActionId,
                },
                ownerReadbackRequired: true,
                inputSchemaHash: insuranceClaimsAssistantClaimCreateInputSchemaHash,
            },
        }, {
            actionId: insuranceClaimsAssistantClaimReviseActionId,
            displayName: 'Update insurance claim',
            targetResourceType: 'insurance-claims.claim',
            inputSchemaHash: insuranceClaimsAssistantClaimReviseInputSchemaHash,
            requiredPermission: 'insurance-claims.claims.manage',
            // A revise appends an immutable claim version. The registered owner
            // action derives the prior content and appends another version.
            assistantPolicy: {
                policyContractVersion: 'platform.assistant-action-policy.v1',
                confirmation: 'explicit',
                effectClass: 'superseding-write',
                externalEffectClass: 'none',
                reversal: {
                    kind: 'superseding-write',
                    registeredReversalActionId: insuranceClaimsAssistantRestoreClaimRevisionActionId,
                },
                ownerReadbackRequired: true,
                inputSchemaHash: insuranceClaimsAssistantClaimReviseInputSchemaHash,
            },
        }, {
            actionId: insuranceClaimsAssistantArchiveCreatedClaimActionId,
            displayName: 'Archive Assistant-created claim',
            targetResourceType: 'insurance-claims.claim',
            inputSchemaHash: insuranceClaimsAssistantArchiveCreatedClaimInputSchemaHash,
            requiredPermission: 'insurance-claims.claims.manage',
            assistantPolicy: {
                policyContractVersion: 'platform.assistant-action-policy.v1',
                confirmation: 'explicit',
                effectClass: 'compensating-write',
                externalEffectClass: 'none',
                reversal: { kind: 'compensating-action', registeredReversalActionId: null },
                ownerReadbackRequired: true,
                inputSchemaHash: insuranceClaimsAssistantArchiveCreatedClaimInputSchemaHash,
            },
        }, {
            actionId: insuranceClaimsAssistantRestoreClaimRevisionActionId,
            displayName: 'Restore prior claim revision',
            targetResourceType: 'insurance-claims.claim',
            inputSchemaHash: insuranceClaimsAssistantRestoreClaimRevisionInputSchemaHash,
            requiredPermission: 'insurance-claims.claims.manage',
            assistantPolicy: {
                policyContractVersion: 'platform.assistant-action-policy.v1',
                confirmation: 'explicit',
                effectClass: 'superseding-write',
                externalEffectClass: 'none',
                reversal: { kind: 'superseding-write', registeredReversalActionId: null },
                ownerReadbackRequired: true,
                inputSchemaHash: insuranceClaimsAssistantRestoreClaimRevisionInputSchemaHash,
            },
        }],
});
export const insuranceClaimsAssistantActionProviderRequestSchema = platformApprovedActionProviderRequestV2Schema;
export const insuranceClaimsAssistantActionProviderResultSchema = platformApprovedActionProviderResultV2Schema;
//# sourceMappingURL=insurance-claims-assistant-actions.contract.js.map